import { brukerApiName } from '@klp/user/config';
import type { Bruker } from '@klp/user/index';
import {
    avsjekkApiName,
    contractToRegistrationApiName,
    instanceApiName,
    kontraktDokumentApiName,
    processApiName,
} from '../config';
import { generateApiRoute } from '@klp/core/api/helpers';
import type { ContractToRegistrationProcessResponse } from '../classes/ContractToRegistrationProcessResponse';

import { baseApi } from '@klp/core/api';
import type { CheckOffContractRequest } from '../classes/CheckOffContractRequest';
import { ContractToRegistration } from '../classes/ContractToRegistration';
import type {
    IContractToRegistration,
    IContractToRegistrationRequest,
} from '../classes';

export enum ConsultantRoles {
    DRIFTS_FORVALTER = 'isDriftsForvalter',
    FORVALTER = 'isForvalter',
    HANDEL_FORVALTER = 'isHandelForvalter',
    KONTOR_FORVALTER = 'isKontorForvalter',
    OKONOMI = 'isOkonomi',
    QA_OKONOMI = 'isQAOkonomi',
}

export enum RegistrationStatuses {
    NY = 'Ny',
    FULLFORT = 'Fullfort',
    HOS_FORVALTER = 'HosForvalter',
    SKAL_IKKE_REGISTRERES = 'SkalIkkeRegistreres',
    REGISTRERING_D365 = 'RegistreringD365',
    TIL_QA = 'TilQA',
    QA_UTFORT = 'QAUtfort',
    TILDELT_OKONOMI = 'TildeltOkonomi',
    TIL_GODKJENNING = 'TilGodkjenning',
    GODKJENT = 'Godkjent',
    GARANTI_REGISTRERING = 'RegistreringAvGaranti',
}

export enum Activities {
    VURDER_RELEVANS = 'Activity_1vr6y7a',
    TILDEL_OKONOMI = 'Activity_1rdcqtp',
    UTFOR_REGISTRERING_D365 = 'Activity_0hw9rg3',
    KONTROLLER_D365_REGISTRERING = 'Activity_1bk3epl',
    GODKJENNING_AV_KONTRAKT = 'Activity_1b84rij',
    TILKNYTT_DOKUMENT_TIL_KONTRAKT = 'Activity_15vq4i6',
    GJENNOMGANGSMOTE = 'Activity_1m3ak5n',
    OPPDATER_KUNDENUMMER_I_SUPER_OFFICE = 'Activity_057tcgr',
}

export enum ConsultantReviewerRoles {
    FORVALTER = 'Forvalter',
    OKONOMI = 'Okonomi',
    QA_OKONOMI = 'Qa',
}

type IDirectLink = {
    id?: string;
    dokumentId?: string;
    konvoluttId?: string;
    kundeLenke?: string;
    kundeNummer?: string;
    kundeNavn?: string;
    lekoId?: string;
    soDokumentId?: number;
    kontraktLenke?: string;
    kontraktNummer?: string;
    kontraktNavn?: string;
};

type IContractToRegistrationResponse = ContractToRegistration;

type ITask = {
    kontraktDokumentId: string;
    oppgaveId: string;
    ansvarlig: string;
    fullforAktivitet: boolean;
    registreringStatus: string;
};

type IProcessTask = Omit<ITask, 'oppgaveId'> & {
    rolle?: ConsultantReviewerRoles;
};

type IExecutableTask = Pick<ITask, 'oppgaveId' | 'registreringStatus'>;

export enum ContractStatisticsKeys {
    NY = 'Ny',
    PAGAAENDE_FORVALTER = 'PagaaendeForvalter',
    PAGAAENDE_OKONOMI = 'PagaaendeOekonomi',
    AVVENTER_GARANTI = 'AvventerGaranti',
}

export type ContractStatisticsResponse = Record<ContractStatisticsKeys, number>;

export type ContractDocumentAggregate = {
    kontraktsDokuments: Array<IContractToRegistration>;
    statusGroupsCount: Record<ContractStatisticsKeys, number>;
    resultCount: number;
    pageSize: number;
    startIndex: number;
};

type Statuses =
    | RegistrationStatuses.NY
    | RegistrationStatuses.FULLFORT
    | RegistrationStatuses.HOS_FORVALTER
    | RegistrationStatuses.SKAL_IKKE_REGISTRERES
    | RegistrationStatuses.REGISTRERING_D365
    | RegistrationStatuses.TIL_QA
    | RegistrationStatuses.QA_UTFORT
    | RegistrationStatuses.TILDELT_OKONOMI
    | RegistrationStatuses.TIL_GODKJENNING
    | RegistrationStatuses.GODKJENT
    | RegistrationStatuses.GARANTI_REGISTRERING;

type IContractToRegistrationStatisticsRequest = {
    ansvarlig?: string;
    region?: string;
    byggNummer?: string;
    statuses?: Array<Statuses>;
};

const registrationStatus = 'registreringStatus';
const assignProcess = 'tildelProsess';
const executeTask = 'utforOppgave';
const directLink = 'direktelenke';
const statistics = 'statistikk';

export const contractToRegistrationApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getContractRegistrationStatistics: build.query<
            ContractStatisticsResponse,
            IContractToRegistrationStatisticsRequest
        >({
            query: ({ ...params }) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    statistics,
                ]),
                params: { ...params },
            }),
            providesTags: ['ContractToRegistrationStatistics'],
        }),
        getContractToRegistrationList: build.query<
            ContractDocumentAggregate,
            IContractToRegistrationRequest
        >({
            query: ({ ...params }) => ({
                url: generateApiRoute([contractToRegistrationApiName]),
                params: { ...params },
            }),
            providesTags: ['ContractToRegistrationList'],
        }),
        getContractRegistrationProcess: build.query<
            ContractToRegistrationProcessResponse,
            {
                kontraktDokumentId: string;
                startNewProcess: boolean;
                culture?: string;
                signalRConnected?: boolean;
            }
        >({
            query: ({ kontraktDokumentId, startNewProcess, culture }) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    processApiName,
                    kontraktDokumentId,
                ]),
                method: 'GET',
                params: {
                    startNewProcess,
                    culture,
                },
            }),
            providesTags: (result, error, { signalRConnected }) =>
                signalRConnected ? [] : ['Process'],
        }),
        getContractRegistrationProcessInstance: build.query<
            ContractToRegistrationProcessResponse,
            {
                processInstanceId: string;
                culture?: string;
            }
        >({
            query: ({ processInstanceId, culture }) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    processApiName,
                    instanceApiName,
                    processInstanceId,
                ]),
                method: 'GET',
                params: {
                    culture,
                },
            }),
        }),
        getRegistrationStatus: build.query<Array<Statuses>, void>({
            query: () =>
                generateApiRoute([
                    contractToRegistrationApiName,
                    processApiName,
                    registrationStatus,
                ]),
        }),
        getContractToRegistration: build.query<
            IContractToRegistrationResponse,
            { kontraktId: string }
        >({
            query: ({ kontraktId }) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    kontraktDokumentApiName,
                    kontraktId,
                ]),
            }),
            transformResponse: (response: IContractToRegistrationResponse) =>
                ContractToRegistration.parseObject(response),
            providesTags: ['ContractToRegistration'],
        }),
        getDirectLink: build.query<IDirectLink, { dokumentId: string }>({
            query: ({ dokumentId }) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    kontraktDokumentApiName,
                    dokumentId,
                    directLink,
                ]),
                method: 'GET',
                headers: { 'Content-Type': 'text/plain' },
            }),
        }),
        assignProcess: build.mutation<
            ContractToRegistrationProcessResponse,
            IProcessTask
        >({
            query(data) {
                const { kontraktDokumentId, ...rest } = data;
                return {
                    url: generateApiRoute([
                        contractToRegistrationApiName,
                        processApiName,
                        assignProcess,
                        kontraktDokumentId,
                    ]),
                    method: 'POST',
                    headers: { 'Content-Type': 'text/plain' },
                    params: { ...rest },
                };
            },
            invalidatesTags: ['Process', 'ContractToRegistrationStatistics'],
        }),
        executeTask: build.mutation<
            ContractToRegistrationProcessResponse,
            IExecutableTask
        >({
            query(data) {
                const { oppgaveId, registreringStatus } = data;
                return {
                    url: generateApiRoute([
                        contractToRegistrationApiName,
                        processApiName,
                        executeTask,
                        oppgaveId,
                    ]),
                    method: 'POST',
                    headers: { 'Content-Type': 'text/plain' },
                    params: { registreringstatus: registreringStatus },
                };
            },
            invalidatesTags: ['Process', 'ContractToRegistrationStatistics'],
        }),
        getConsultants: build.query<Array<Bruker>, void>({
            query: () => generateApiRoute([brukerApiName]),
        }),
        registerContract: build.mutation<
            unknown,
            CheckOffContractRequest & { skipCacheProcessInvalidation?: boolean }
        >({
            query: ({
                skipCacheProcessInvalidation: _skipCacheProcessInvalidation,
                ...rest
            }) => ({
                url: generateApiRoute([
                    contractToRegistrationApiName,
                    avsjekkApiName,
                ]),
                method: 'POST',
                body: rest,
                headers: { 'Content-Type': 'application/json' },
            }),
            invalidatesTags: (
                result,
                error,
                { skipCacheProcessInvalidation },
            ) => {
                const tagsToInvalidate = [
                    'ContractToRegistration',
                    'ContractToRegistrationList',
                    'ContractToRegistrationStatistics',
                ] as const;
                return skipCacheProcessInvalidation
                    ? tagsToInvalidate
                    : ['Process', ...tagsToInvalidate];
            },
        }),
    }),
});

export const {
    useGetContractRegistrationProcessQuery,
    useGetContractRegistrationProcessInstanceQuery,
    useAssignProcessMutation,
    useExecuteTaskMutation,
    useGetRegistrationStatusQuery,
    useGetConsultantsQuery,
    useGetContractToRegistrationQuery,
    useLazyGetContractToRegistrationQuery,
    useLazyGetConsultantsQuery,
    useLazyGetContractRegistrationProcessQuery,
    useRegisterContractMutation,
    useGetDirectLinkQuery,
    useGetContractRegistrationStatisticsQuery,
    useGetContractToRegistrationListQuery,
} = contractToRegistrationApi;
